'use client';

import { useAuth } from '@/backend/context/auth-management';
import { useFinance, useMoney } from '@/backend/firebase/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/PageHeader';
import { formatTimeElapsed } from '@/lib/date-helpers';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';
import type { CurrenciesType } from '@/shared/schema';
import {
  ArrowDownLeft,
  Banknote,
  History,
  RefreshCcw,
  TrendingUp,
  Landmark,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ExpenseModal } from '../global-modals/ExpenseModal';

export default function BusinessFinanceDashboard() {
  const { activeBusiness, profile } = useAuth();
  const navigate = useNavigate();
  const paymentMethods = activeBusiness?.public?.finance?.paymentMethods || [];

  const businessId = activeBusiness?.id || profile?.id || '';

  const { format, toBs } = useMoney();
  const accountingCurrency = (activeBusiness?.currencyConfig
    ?.accountingCurrency || 'USD') as CurrenciesType;

  const { data: globalBalance } = useFinance.useBalance(businessId, 'main');

  const [isExpenseDialogOpen] = useState(false); // Legacy modal removed or will be simplified

  const timeElapsed = useMemo(() => {
    if (!globalBalance?.last_settlement_at) return 'Periodo Nuevo';
    const timestamp = globalBalance.last_settlement_at.seconds
      ? globalBalance.last_settlement_at.seconds * 1000
      : globalBalance.last_settlement_at;
    return formatTimeElapsed(timestamp);
  }, [globalBalance?.last_settlement_at]);

  return (
    <div className="min-h-screen bg-zinc-50 pb-32">
      <main className="mx-auto max-w-md space-y-6 px-4 pt-6">
        {/* HEADER */}
        <PageHeader
          title="Ventas y Finanzas"
          subtitle="Motor de Ingresos"
          showBack={true}
        />

        {/* TARJETA MAESTRA: RENDIMIENTO DE VENTAS */}
        <Card className="relative overflow-hidden rounded-[2.5rem] border-none bg-zinc-900 text-white shadow-2xl">
          <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-emerald-500/10 blur-3xl" />
          <CardContent className="relative z-10 flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-[10px] font-black tracking-widest text-emerald-400 uppercase">
                <TrendingUp className="h-4 w-4" /> Resultados Reales
              </p>
              <div className="rounded-xl border border-white/10 bg-white/10 px-3 py-1.5 backdrop-blur-md">
                <span className="text-[9px] font-black tracking-widest uppercase">
                  {timeElapsed}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <p className="text-[11px] font-black tracking-[0.2em] text-white/50 uppercase">
                Utilidad Neta del Negocio
              </p>
              <div className="flex flex-col">
                <p className="truncate text-5xl font-black tracking-tighter italic">
                  {format(
                    globalBalance?.period_net_accounting || 0,
                    accountingCurrency,
                  )}
                </p>
                {profile?.currencyConfig.multiCurrency &&
                  ['USD', 'EUR', 'BS'].includes(accountingCurrency) && (
                    <p className="mt-1 text-sm font-black text-emerald-400 uppercase italic opacity-80">
                      ≈{' '}
                      {format(
                        toBs(
                          globalBalance?.period_net_accounting || 0,
                          accountingCurrency,
                        ),
                        'BS',
                      )}
                    </p>
                  )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 border-t border-white/10 pt-6">
              <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
                <p className="mb-1 text-[8px] font-black uppercase opacity-40">
                  Total Bruto
                </p>
                <p className="text-base font-black italic">
                  {format(
                    globalBalance?.period_gross_accounting || 0,
                    accountingCurrency,
                  )}
                </p>
              </div>
              <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
                <p className="mb-1 text-[8px] font-black uppercase opacity-40">
                  Costo Directo
                </p>
                <p className="text-base font-black text-rose-400 italic">
                  -
                  {format(
                    globalBalance?.period_materials_accounting || 0,
                    accountingCurrency,
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ACCIONES DE CIERRE */}
        <div className="grid grid-cols-1 gap-3">
          <Button
            id="close-batch-button"
            className="group flex h-16 w-full flex-col items-center justify-center gap-0 rounded-[1.8rem] border-none bg-zinc-900 px-5 transition-all hover:bg-zinc-800 active:scale-95 shadow-xl"
            onClick={() => navigate(ROUTES.FINANCES.SETTLE(businessId))}
          >
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-white/10 p-1.5 transition-colors group-hover:bg-white/20">
                <RefreshCcw className="h-4 w-4 text-emerald-400" strokeWidth={3} />
              </div>
              <span className="text-[10px] font-black tracking-widest text-zinc-400 uppercase">
                Reiniciar Métricas
              </span>
            </div>
            <p className="-mt-0.5 text-sm font-black tracking-tighter text-white uppercase italic">
              Cerrar Periodo de Ventas
            </p>
          </Button>
        </div>

        {/* HISTORIAL */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate(ROUTES.FINANCES.SETTLEMENTS)}
            className="flex w-full flex-col justify-between rounded-[2rem] border border-zinc-100 bg-white p-5 shadow-sm transition-all active:scale-[0.98]"
          >
            <div className="mb-4 w-max rounded-2xl bg-zinc-100 p-3">
              <History className="h-5 w-5 text-zinc-400" />
            </div>
            <div className="w-full text-left">
              <p className="text-sm leading-tight font-black tracking-tighter uppercase italic text-zinc-900">
                Historial de
                <br />
                Ventas
              </p>
            </div>
          </button>

          <button
            onClick={() => navigate(ROUTES.FINANCES.EXPENSES)}
            className="flex w-full flex-col justify-between rounded-[2rem] border border-zinc-100 bg-white p-5 shadow-sm transition-all active:scale-[0.98]"
          >
            <div className="mb-4 w-max rounded-2xl bg-rose-50 p-3">
              <Banknote className="h-5 w-5 text-rose-400" />
            </div>
            <div className="w-full text-left">
              <p className="text-sm leading-tight font-black tracking-tighter uppercase italic text-zinc-900">
                Gastos y
                <br />
                Egresos
              </p>
            </div>
          </button>
        </div>

        {/* BÓVEDA POR MÉTODO */}
        <section className="space-y-3">
          <h3 className="ml-2 text-[10px] font-black tracking-[0.2em] text-zinc-400 uppercase">
            Efectivo por Método de Pago
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {paymentMethods.map((method) => {
              const amount = globalBalance?.method_balances?.[method.id!] || 0;
              if (amount <= 0) return null;
              return (
                <Card
                  key={method.id}
                  className="rounded-[1.8rem] border-none bg-white shadow-sm"
                >
                  <CardContent className="p-5">
                    <div className="mb-3 flex items-center gap-2 text-zinc-400">
                      {method.currency === 'BS' ? (
                        <Landmark className="h-3.5 w-3.5" />
                      ) : (
                        <Banknote className="h-3.5 w-3.5" />
                      )}
                      <span className="truncate text-[8px] font-black tracking-widest uppercase">
                        {method.title}
                      </span>
                    </div>
                    <p className="text-lg font-black italic text-zinc-900">
                      {format(amount, method.currency as CurrenciesType)}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}
